import { useEffect, useMemo, useReducer, useRef } from "react";
import { toast } from "sonner";
import { PATHS } from "@/lib/content";
import { QUESTION_FLOWS } from "@/lib/questionFlows";
import { submitInquiry } from "@/lib/api";
import { validateInquiry } from "@/lib/formValidation";
import { buildInquiryPayload, canAdvanceQuestion, createJourney, journeyReducer, summarizeAnswers } from "./journeyModel";

export function useInquiryJourney(open, initialPath) {
  const [state, dispatch] = useReducer(journeyReducer, initialPath, createJourney);
  const requestVersion = useRef(0);
  const { pathId, answers, qIndex, form, submitting } = state;

  useEffect(() => {
    if (open) dispatch({ type: "reset", initialPath });
    // A result from a closed/reopened journey must not advance the new session.
    requestVersion.current += 1;
    return () => { requestVersion.current += 1; };
  }, [open, initialPath]);

  const selectedPath = PATHS.find((path) => path.id === pathId);
  const visibleQuestions = useMemo(() => (QUESTION_FLOWS[pathId] || [])
    .filter((question) => !question.showIf || question.showIf(answers)), [pathId, answers]);
  const currentQuestion = visibleQuestions[qIndex];
  const summary = useMemo(() => summarizeAnswers(visibleQuestions, answers), [visibleQuestions, answers]);
  const canAdvance = canAdvanceQuestion(currentQuestion, answers);
  const patch = (values) => dispatch({ type: "patch", values });

  const nextQuestion = () => {
    if (!canAdvance) return;
    if (qIndex < visibleQuestions.length - 1) patch({ qIndex: qIndex + 1 });
    else patch({ step: "details" });
  };
  const prevQuestion = () => {
    if (qIndex > 0) patch({ qIndex: qIndex - 1 });
    else patch({ step: "select" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const errors = validateInquiry(form, pathId);
    patch({ errors });
    if (Object.keys(errors).length) return;
    const version = requestVersion.current;
    patch({ submitting: true });
    try {
      await submitInquiry(buildInquiryPayload(state));
      if (version === requestVersion.current) patch({ step: "done" });
    } catch {
      if (version === requestVersion.current) toast.error("Something went wrong. Please try again.");
    } finally {
      if (version === requestVersion.current) patch({ submitting: false });
    }
  };

  return { ...state, selectedPath, visibleQuestions, currentQuestion, summary, canAdvance,
    nextQuestion, prevQuestion, handleSubmit,
    choosePath: (id) => dispatch({ type: "choose", pathId: id }),
    setAnswer: (id, value) => dispatch({ type: "answer", id, value }),
    toggleMulti: (id, value) => dispatch({ type: "multi", id, value }),
    updateField: (field, value) => dispatch({ type: "field", field, value }),
    backToQuestions: () => patch({ step: "questions" }),
  };
}